'use client';

import { useEffect, useState } from 'react';
import { Sparkles, LayoutGrid, BookOpen } from 'lucide-react';
import { Link, usePathname } from '@/i18n/routing';
import { api } from '@/lib/api';

// Nav labels are intentionally in English for a clean, international look; page
// content still follows the selected locale. Account/settings live behind the
// header avatar menu; News is folded into Market (per-asset related headlines),
// so the AI analysis is the primary landing tab.
const items = [
  { href: '/stocks', label: 'AI', Icon: Sparkles },
  { href: '/market', label: 'Market', Icon: LayoutGrid },
  { href: '/columns', label: 'Columns', Icon: BookOpen },
] as const;

export default function BottomNav() {
  const pathname = usePathname();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Re-check per navigation so an admin→member switch (client-side nav, no
    // reload) doesn't leave isAdmin stale-true and hide the member's tabs.
    api.me().then((u) => setIsAdmin(u.role === 'admin')).catch(() => setIsAdmin(false));
  }, [pathname]);

  // No consumer tabs on login or the admin dashboard (path check is
  // synchronous, so it never flashes before the role loads).
  if (pathname === '/login' || pathname === '/reset' || pathname.startsWith('/admin') || isAdmin)
    return null;

  return (
    <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-zinc-200 bg-white/80 backdrop-blur-lg dark:border-zinc-800 dark:bg-zinc-900/80">
      <ul className="mx-auto flex max-w-md items-stretch justify-around px-1 pb-[env(safe-area-inset-bottom)]">
        {items.map(({ href, label, Icon }) => {
          const active =
            pathname === href ||
            (href === '/stocks' && pathname.startsWith('/stocks')) ||
            (href === '/market' && pathname === '/');
          return (
            <li key={href} className="flex-1">
              <Link
                href={href}
                className={`group flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                  active ? 'text-brand' : 'text-zinc-400 dark:text-zinc-500'
                }`}
              >
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-2xl transition-all ${
                    active
                      ? 'bg-brand/10 text-brand'
                      : 'text-zinc-500 group-active:bg-zinc-100 dark:text-zinc-400 dark:group-active:bg-zinc-800'
                  }`}
                >
                  <Icon size={20} strokeWidth={active ? 2.4 : 1.9} />
                </span>
                {label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
